import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Grid,
  Stack,
  Button,
  Drawer,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  FormHelperText,
  CircularProgress,
  TextField,
  IconButton,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

// components
import {
  Close,
  UploadFile,
  Download,
  ArrowBack,
  ArrowForward,
  People,
  PersonAdd,
  PersonOff,
  CheckCircle,
  Verified,
  RemoveCircle,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridToolbarExport } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useGetOrganizationsQuery } from "../../../redux/services/organization.service";
import { useGetPlansQuery } from "../../../redux/services/plans.service";
import {
  useBulkSubscriptionOrganizationMutation,
  useCheckBulkSubscriptionOrganizationMutation,
} from "../../../redux/services/subscription.service";
import { useGetEventPlacesQuery } from "../../../redux/services/device.service";
import Scrollbar from "../../../components/scrollbar";
import CustomDialog from "../../../components/dialog/CustomDialog";
import { OrganizationSubscriptionSchema } from "../../../forms-schema/form.schema";

OrganizationSubscriptionForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

const supportedFileTypes = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const STEPS = ["Upload & Check", "Configure & Submit"];

export default function OrganizationSubscriptionForm({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEventPlace, setSelectedEventPlace] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);

  // Check phase states
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkS3Url, setCheckS3Url] = useState("");

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
  } = useForm({
    resolver: yupResolver(OrganizationSubscriptionSchema),
  });

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (!open) {
      reset({
        organizationId: "",
        planId: "",
        duration: "",
        eventPlace: "",
        tag: "",
        donationDate: null,
        documentPagesLimit: "",
      });
      setSelectedFile(null);
      setFileError("");
      setSelectedEventPlace("");
      setFilteredTags([]);
      setActiveStep(0);
      // Reset check states
      setCheckResult(null);
      setCheckS3Url("");
    }
  }, [open, reset]);

  const {
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
    data: organizations,
  } = useGetOrganizationsQuery(null, {
    skip: !open,
  });

  const {
    isLoading: isPlansLoading,
    isFetching: isPlansFetching,
    data: plans,
  } = useGetPlansQuery(undefined, {
    skip: !open,
  });

  const {
    data: eventPlaces,
    isSuccess: isSuccessEventPlaces,
    isLoading: isLoadingEventPlaces,
  } = useGetEventPlacesQuery(
    {
      organizationId: watch("organizationId"),
    },
    {
      skip: !open || !watch("organizationId"),
    },
  );

  const [bulkSubscriptionOrganization] =
    useBulkSubscriptionOrganizationMutation();
  const [checkBulkSubscriptionOrganization] =
    useCheckBulkSubscriptionOrganizationMutation();

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openDuplicateUsersDialog, setOpenDuplicateUsersDialog] =
    useState(false);
  const [duplicateUsers, setDuplicateUsers] = useState([]);
  const [duplicateExcelUrl, setDuplicateExcelUrl] = useState("");
  const [openResultsDialog, setOpenResultsDialog] = useState(false);
  const [subscriptionResults, setSubscriptionResults] = useState({
    rejectedSubscription: [],
    upgradedSubscription: [],
    newSubscriptionUsers: [],
    s3Url: "",
    invoicePdfUrl: "",
  });

  // Phase 1: Check file handler
  const handleCheckFile = async () => {
    if (!selectedFile) {
      setFileError("Please select a file first");
      return;
    }

    setCheckLoading(true);
    try {
      const result = await checkBulkSubscriptionOrganization({
        file: selectedFile,
      });

      if (result?.data?.success) {
        const responseData = result.data.data;
        setCheckResult(responseData?.summary || null);
        setCheckS3Url(responseData?.s3Url || "");
        enqueueSnackbar(
          "File checked successfully! Review the results below.",
          {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
      } else if (result?.error) {
        const errorData = result?.error?.data;
        enqueueSnackbar(errorData?.message || "Failed to check file", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
      }
    } catch (error) {
      console.error("Error checking bulk subscription:", error);
      enqueueSnackbar("An error occurred. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    } finally {
      setCheckLoading(false);
    }
  };

  // Phase 2: Submit handler
  const handleClick = async (values) => {
    setLoading(true);
    try {
      const payload = {
        file: selectedFile,
        planId: values.planId,
        organizationId: values.organizationId,
        ...(values.duration && { duration: values.duration }),
        ...(values.eventPlace && { eventPlace: values.eventPlace }),
        ...(values.tag && { tag: values.tag }),
        ...(values.donationDate && {
          donationDate: moment(values.donationDate).format("YYYY-MM-DD"),
        }),
        ...(values.documentPagesLimit && {
          documentPagesLimit: values.documentPagesLimit,
        }),
      };

      const result = await bulkSubscriptionOrganization(payload);

      if (result?.data?.success) {
        enqueueSnackbar(
          result?.data?.message || "Subscription created successfully",
          {
            variant: "success",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          },
        );

        // Show results dialog with subscription details
        const responseData = result?.data?.data;
        setSubscriptionResults({
          rejectedSubscription: responseData?.rejectedSusbcription || [],
          upgradedSubscription: responseData?.upgradedSubscription || [],
          newSubscriptionUsers: responseData?.newSubscriptionUsers || [],
          s3Url: responseData?.s3Url || "",
          invoicePdfUrl: responseData?.invoicePdfUrl || "",
        });
        setOpenResultsDialog(true);

        reset();
        setSelectedFile(null);
        setOpenConfirmationDialog(false);
      } else if (result?.error) {
        const errorData = result?.error?.data;

        // Handle duplicate users
        if (errorData?.statusCode === 400 && errorData?.data?.duplicateUsers) {
          setDuplicateUsers(errorData?.data?.duplicateUsers || []);
          setDuplicateExcelUrl(errorData?.data?.excelUrl || "");
          setOpenDuplicateUsersDialog(true);
          setOpenConfirmationDialog(false);
          enqueueSnackbar(errorData?.message || "Duplicate users found", {
            variant: "warning",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        } else {
          enqueueSnackbar(errorData?.message || "Something went wrong", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
          setOpenConfirmationDialog(false);
        }
      }
    } catch (error) {
      console.error("Error occurred during bulk subscription:", error);
      enqueueSnackbar("An error occurred. Please try again later.", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const fileRef = useRef();

  const handleFileChange = (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSelectedFile(null);
        enqueueSnackbar("File size exceeds 10MB limit", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar(
          "Unsupported file format. Please upload .xlsx or .xls file",
          {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          },
        );
        return;
      }

      setFileError("");
      setSelectedFile(file);
      // Reset check result when new file is selected
      setCheckResult(null);
      setCheckS3Url("");
    } catch (error) {
      console.error("Error while handling file change:", error);
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const file = e.dataTransfer.files[0];

      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSelectedFile(null);
        enqueueSnackbar("File size exceeds 10MB limit", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        return;
      }

      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar(
          "Unsupported file format. Please upload .xlsx or .xls file",
          {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          },
        );
        return;
      }

      setFileError("");
      setSelectedFile(file);
      // Reset check result when new file is selected
      setCheckResult(null);
      setCheckS3Url("");
    } catch (error) {
      console.error("Error while handling file drop:", error);
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
  };

  const handleOpenConfirmationDialog = () => {
    if (!selectedFile) {
      setFileError("Please select a file");
      return;
    }
    setOpenConfirmationDialog(true);
  };

  const handleEventPlaceChange = (newValue) => {
    try {
      const selectedPlace = newValue || "";
      setSelectedEventPlace(selectedPlace);
      setValue("eventPlace", selectedPlace);

      const tags =
        eventPlaces?.data?.allEventPlaces?.filter(
          (eventItem) =>
            eventItem.eventPlace === selectedPlace && eventItem.tag !== null,
        )[0]?.tags || [];

      setFilteredTags(tags);
      setValue("tag", "");
    } catch (error) {
      console.error("Error while handling event place change:", error);
    }
  };

  const handleTagChange = (newValue) => {
    setValue("tag", newValue || "");
  };

  // Handle plan selection and prefill duration/documentPagesLimit
  const handlePlanChange = (planId) => {
    setValue("planId", planId);
    const selectedPlan = organizationPlans?.find((plan) => plan.id === planId);
    if (selectedPlan) {
      if (selectedPlan.durationDays) {
        setValue("duration", String(selectedPlan.durationDays));
      }
      if (selectedPlan.documentPagesLimit) {
        setValue("documentPagesLimit", String(selectedPlan.documentPagesLimit));
      }
    }
  };

  // Filter plans by CUSTOM type (for organization subscriptions)
  const organizationPlans = plans?.data?.filter(
    (plan) => plan.type === "CUSTOM",
  );

  const handleNextStep = () => {
    if (activeStep === 0 && !checkResult) {
      enqueueSnackbar("Please check the file first before proceeding", {
        variant: "warning",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
      return;
    }
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setActiveStep(0);
  };

  // Render Phase 1: Upload & Check
  const renderPhase1 = () => (
    <Stack spacing={3}>
      <Alert severity="info" sx={{ py: 1 }}>
        <Typography variant="body2">
          <strong>Step 1:</strong> Upload Excel file and check user data before
          proceeding with subscription.
        </Typography>
      </Alert>

      {/* File Upload */}
      <Box>
        <InputLabel htmlFor="file" sx={{ mb: 1 }}>
          Excel File *
        </InputLabel>

        <input
          onChange={handleFileChange}
          ref={fileRef}
          type="file"
          accept=".xlsx, .xls"
          hidden
          data-testid="file-input"
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px dashed #D9D9D9",
            borderRadius: "10px",
            padding: "20px",
            justifyContent: "center",
            flexDirection: "column",
            cursor: "pointer",
            height: 180,
            "&:hover": { backgroundColor: "#F5F5F5" },
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadFile sx={{ fontSize: "50px", color: "#D9D9D9" }} />
          <Typography
            variant="body2"
            sx={{ marginTop: 0.5, textAlign: "center", fontStyle: "italic" }}
          >
            {selectedFile ? selectedFile.name : "(No file selected)"}
          </Typography>
          <Button
            sx={{ marginTop: 2, boxShadow: "none" }}
            variant="outlined"
            onClick={() => fileRef.current.click()}
          >
            Browse
          </Button>
          <Typography
            variant="body2"
            sx={{ marginTop: 0.5, textAlign: "center" }}
          >
            Or drag here{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontStyle: "italic" }}
            >
              (.xlsx, .xls - max 10MB)
            </Typography>
          </Typography>
        </Box>
        {fileError && (
          <FormHelperText error={Boolean(fileError)}>
            {fileError}
          </FormHelperText>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Required headers: First Name, Last Name, Contact Number, Email, Date
          of Birth, Gender, City, State, Country, Pincode
        </Typography>

        <a
          href="https://sunbots-people.s3.ap-south-1.amazonaws.com/1724828563100_1710311771521_SmartOn%20User%20Form.xlsx"
          style={{
            textTransform: "none",
            textDecoration: "underline",
            color: "#1976d2",
            cursor: "pointer",
            marginTop: "8px",
            display: "inline-block",
          }}
          rel="noreferrer"
          target="_blank"
        >
          Download Template
        </a>
      </Box>

      {/* Check Button */}
      <LoadingButton
        variant="contained"
        onClick={handleCheckFile}
        loading={checkLoading}
        disabled={!selectedFile}
        fullWidth
        size="large"
      >
        Check File
      </LoadingButton>

      {/* Check Results */}
      {checkResult && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {/* Download Excel Button */}
          {checkS3Url && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => window.open(checkS3Url, "_blank")}
              fullWidth
              sx={{ mb: 2 }}
            >
              Download Detailed Report (Excel)
            </Button>
          )}

          {/* Summary Cards */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            📊 Summary
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "primary.50" }}
              >
                <People color="primary" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.totalUsersInExcel || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total in Excel
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "info.50" }}>
                <PersonAdd color="info" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.uniqueContactNumbers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Unique Contacts
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "success.50" }}
              >
                <CheckCircle color="success" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.usersFoundInDatabase || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Found in DB
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "error.50" }}>
                <PersonOff color="error" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.usersNotFoundInDatabase || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not in DB
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "secondary.50" }}
              >
                <Verified color="secondary" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.usersWithActiveSubscriptions || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active Subs
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "warning.50" }}
              >
                <RemoveCircle color="warning" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.usersWithoutSubscriptions || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No Subs
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Warnings */}
          {checkResult.duplicateContactNumbers > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{checkResult.duplicateContactNumbers}</strong> duplicate
                contact numbers found in the Excel file.
              </Typography>
            </Alert>
          )}

          {checkResult.usersWithAuthenticatedSubscriptions > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>
                  {checkResult.usersWithAuthenticatedSubscriptions}
                </strong>{" "}
                users have authenticated subscriptions.
              </Typography>
            </Alert>
          )}

          {/* Info about what will happen */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Only <strong>new users</strong> and users
              with <strong>ongoing organization subscriptions</strong> will
              receive this subscription. All other users will be{" "}
              <strong>rejected</strong>.
            </Typography>
          </Alert>

          {/* Next Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleNextStep}
            endIcon={<ArrowForward />}
            sx={{ mt: 3 }}
          >
            Proceed to Step 2
          </Button>
        </Box>
      )}
    </Stack>
  );

  // Render Phase 2: Configure & Submit
  const renderPhase2 = () => (
    <Stack spacing={3}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={handleBackStep}
        sx={{ alignSelf: "flex-start" }}
      >
        Back to Step 1
      </Button>

      <Alert severity="info" sx={{ py: 1 }}>
        <Typography variant="body2">
          <strong>Step 2:</strong> Configure subscription details for{" "}
          <strong>{selectedFile?.name}</strong>
        </Typography>
      </Alert>

      {/* File & Summary Info */}
      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <UploadFile color="primary" />
            <Box>
              <Typography variant="subtitle2">{selectedFile?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {checkResult?.totalUsersInExcel || 0} users •{" "}
                {checkResult?.uniqueContactNumbers || 0} unique contacts
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Organization Select */}
      <FormControl fullWidth>
        <InputLabel
          id="organization-label"
          error={Boolean(errors.organizationId)}
        >
          Organization *
        </InputLabel>
        <Select
          {...register("organizationId", { required: true })}
          labelId="organization-label"
          id="organization-select"
          label="Organization *"
          inputProps={{ "data-testid": "organizationId" }}
          error={Boolean(errors.organizationId)}
          value={watch("organizationId") || ""}
        >
          {isOrgLoading || isOrgFetching ? (
            <MenuItem
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            organizations?.data
              ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((org) => (
                <MenuItem key={org?.id} value={org?.id}>
                  {org?.name}
                </MenuItem>
              ))
          )}
        </Select>
        <FormHelperText error={Boolean(errors.organizationId)}>
          {errors.organizationId?.message}
        </FormHelperText>
      </FormControl>

      {/* Plan Select */}
      <FormControl fullWidth>
        <InputLabel id="plan-label" error={Boolean(errors.planId)}>
          Plan *
        </InputLabel>
        <Select
          {...register("planId", { required: true })}
          labelId="plan-label"
          id="plan-select"
          label="Plan *"
          inputProps={{ "data-testid": "planId" }}
          error={Boolean(errors.planId)}
          value={watch("planId") || ""}
          onChange={(e) => handlePlanChange(e.target.value)}
        >
          {isPlansLoading || isPlansFetching ? (
            <MenuItem
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            organizationPlans
              ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((plan) => (
                <MenuItem key={plan?.id} value={plan?.id}>
                  {plan?.name}
                </MenuItem>
              ))
          )}
        </Select>
        <FormHelperText error={Boolean(errors.planId)}>
          {errors.planId?.message}
        </FormHelperText>
      </FormControl>

      {/* Duration */}
      <TextField
        {...register("duration")}
        label="Duration (in days)"
        type="number"
        fullWidth
        error={Boolean(errors.duration)}
        helperText={
          errors.duration?.message || "Number of days for subscription duration"
        }
        inputProps={{ min: 1 }}
      />

      {/* Document Pages Limit */}
      <TextField
        {...register("documentPagesLimit")}
        label="Document Pages Limit"
        type="number"
        fullWidth
        error={Boolean(errors.documentPagesLimit)}
        helperText={
          errors.documentPagesLimit?.message ||
          "Maximum number of document pages allowed"
        }
        inputProps={{ min: 1 }}
      />

      <Divider />

      {/* Event Place - Autocomplete with freeSolo */}
      <Autocomplete
        freeSolo
        id="eventPlace-autocomplete"
        options={
          isLoadingEventPlaces
            ? []
            : eventPlaces?.data?.allEventPlaces
                ?.filter((eventPlace) => eventPlace?.eventPlace && typeof eventPlace.eventPlace === "string" && eventPlace.eventPlace.trim() !== "")
                ?.map((eventPlace) => eventPlace.eventPlace) || []
        }
        getOptionLabel={(option) => option || ""}
        fullWidth
        value={selectedEventPlace || ""}
        onChange={(event, newValue) => handleEventPlaceChange(newValue)}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            handleEventPlaceChange(newInputValue);
          }
        }}
        disabled={!watch("organizationId")}
        loading={isLoadingEventPlaces}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Event Place"
            fullWidth
            error={Boolean(errors.eventPlace)}
            helperText={errors.eventPlace?.message}
            inputProps={{
              ...params.inputProps,
              "data-testid": "eventPlace",
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingEventPlaces ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Tag - Autocomplete with freeSolo */}
      <Autocomplete
        freeSolo
        id="tag-autocomplete"
        options={filteredTags.length > 0 ? filteredTags : []}
        getOptionLabel={(option) => option || ""}
        fullWidth
        value={watch("tag") || ""}
        onChange={(event, newValue) => handleTagChange(newValue)}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            handleTagChange(newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tag"
            fullWidth
            error={Boolean(errors.tag)}
            helperText={errors.tag?.message}
            inputProps={{
              ...params.inputProps,
              "data-testid": "tag",
            }}
          />
        )}
      />

      {/* Donation Date with Calendar */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Controller
          name="donationDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              label="Donation Date"
              format="DD/MM/YYYY"
              value={field.value ? moment(field.value) : null}
              onChange={(date) => {
                setValue("donationDate", date ? date.toDate() : null);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(errors.donationDate),
                  helperText: errors.donationDate?.message,
                },
              }}
            />
          )}
        />
      </LocalizationProvider>

      {/* Warning */}
      <Alert severity="warning" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Note:</strong> Only <strong>new users</strong> and users with{" "}
          <strong>ongoing organization subscriptions</strong> will receive this
          subscription. All other users will be <strong>rejected</strong>.
        </Typography>
      </Alert>

      {/* Submit Button */}
      <LoadingButton
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleSubmit(handleOpenConfirmationDialog)}
        loading={loading}
        size="large"
        fullWidth
      >
        Submit
      </LoadingButton>
    </Stack>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450 },
            border: "none",
            overflow: "hidden",
          },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Organization Subscription</Typography>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
            <Divider />

            {/* Stepper */}
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Phase Content */}
            {activeStep === 0 ? renderPhase1() : renderPhase2()}
          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Confirmation Dialog */}
      <CustomDialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        title="Confirm Organization Subscription"
        description={`Are you sure you want to proceed with bulk subscription for ${
          checkResult?.uniqueContactNumbers || 0
        } users from "${selectedFile?.name}"?`}
        onConfirm={handleSubmit(handleClick)}
        isLoading={loading}
      />

      {/* Duplicate Users Dialog */}
      <CustomDialog
        open={openDuplicateUsersDialog}
        title="Duplicate Users Found"
        description={
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", marginBottom: 2 }}
            >
              The following duplicate users were found in the Excel file. Please
              remove duplicates and try again.
            </Typography>
            {duplicateExcelUrl && (
              <Button
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={() => window.open(duplicateExcelUrl, "_blank")}
              >
                Download Duplicate Users Excel
              </Button>
            )}
            {duplicateUsers?.length > 0 && (
              <DataGrid
                columns={[
                  {
                    field: "firstName",
                    headerName: "First Name",
                    width: 120,
                  },
                  {
                    field: "lastName",
                    headerName: "Last Name",
                    width: 120,
                  },
                  {
                    field: "contactNumber",
                    headerName: "Contact Number",
                    width: 150,
                  },
                  {
                    field: "count",
                    headerName: "Count",
                    width: 80,
                  },
                ]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                rows={duplicateUsers.map((user, index) => ({
                  ...user,
                  id: index,
                }))}
                sx={{ width: "100%" }}
                getRowId={(row) => row?.id}
                disableRowSelectionOnClick
                filterMode="client"
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="client"
                sortingMode="client"
                scrollbarSize={1}
                autoHeight
                slots={{
                  toolbar: GridToolbarExport,
                }}
              />
            )}
          </>
        }
        cancelButton={false}
        confirmText="Close"
        onConfirm={() => {
          setOpenDuplicateUsersDialog(false);
          setDuplicateUsers([]);
          setDuplicateExcelUrl("");
        }}
        isLoading={loading}
      />

      {/* Subscription Results Dialog */}
      <CustomDialog
        open={openResultsDialog}
        title="Bulk Subscription Results"
        description={
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", marginBottom: 2 }}
            >
              Bulk subscription process completed. Here are the results:
            </Typography>

            {(subscriptionResults.s3Url ||
              subscriptionResults.invoicePdfUrl) && (
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {subscriptionResults.s3Url && (
                  <Button
                    variant="contained"
                    onClick={() =>
                      window.open(subscriptionResults.s3Url, "_blank")
                    }
                  >
                    Download Results Excel
                  </Button>
                )}
                {subscriptionResults.invoicePdfUrl && (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(subscriptionResults.invoicePdfUrl, "_blank")
                    }
                  >
                    Download Invoice PDF
                  </Button>
                )}
              </Stack>
            )}

            {/* New Subscriptions */}
            {subscriptionResults.newSubscriptionUsers?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "success.main" }}
                >
                  New Subscriptions (
                  {subscriptionResults.newSubscriptionUsers.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 120,
                    },
                    { field: "lastName", headerName: "Last Name", width: 120 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 130,
                    },
                    { field: "email", headerName: "Email", width: 180 },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.newSubscriptionUsers.map(
                    (user, index) => ({
                      ...user,
                      id: user.id || index,
                    }),
                  )}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Upgraded Subscriptions */}
            {subscriptionResults.upgradedSubscription?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "info.main" }}
                >
                  Upgraded Subscriptions (
                  {subscriptionResults.upgradedSubscription.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 120,
                    },
                    { field: "lastName", headerName: "Last Name", width: 120 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 130,
                    },
                    { field: "email", headerName: "Email", width: 180 },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.upgradedSubscription.map(
                    (user, index) => ({
                      ...user,
                      id: user.id || index,
                    }),
                  )}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Rejected Subscriptions */}
            {subscriptionResults.rejectedSubscription?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "error.main" }}
                >
                  Rejected Subscriptions (
                  {subscriptionResults.rejectedSubscription.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 120,
                    },
                    { field: "lastName", headerName: "Last Name", width: 120 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 130,
                    },
                    { field: "reason", headerName: "Reason", width: 200 },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.rejectedSubscription.map(
                    (user, index) => ({
                      ...user,
                      id: user.id || index,
                    }),
                  )}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Summary if no data in any category */}
            {subscriptionResults.newSubscriptionUsers?.length === 0 &&
              subscriptionResults.upgradedSubscription?.length === 0 &&
              subscriptionResults.rejectedSubscription?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No detailed results available. Please download the Excel file
                  for more information.
                </Typography>
              )}
          </>
        }
        cancelButton={false}
        confirmText="Close"
        onConfirm={() => {
          setOpenResultsDialog(false);
          setSubscriptionResults({
            rejectedSubscription: [],
            upgradedSubscription: [],
            newSubscriptionUsers: [],
            s3Url: "",
            invoicePdfUrl: "",
          });
          onClose();
        }}
        isLoading={loading}
      />
    </>
  );
}
